import { graphql, PageProps } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { LayoutProvider } from '@application/Contexts/LayoutContext'
import { PageBuilder } from '@application/Interfaces/Features/PageBuilder/PageBuilder'
import type {
    BlogListItemType,
    BlogListType,
    CategoryType,
    PageNodeType,
    SingleTypeComponentType,
} from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { isGdprCookieAccepted } from '@infrastructure/Cookies'
import { GrowthBookFeaturesType } from '@infrastructure/Growthbook'
import { growthbookTrackingCallback } from '@infrastructure/Growthbook/utils'
import { StrapiTranslations, Translations } from '@src/Application/Types/TranslationTypes'
import { STRAPI_COMPONENT_TYPES } from '@src/Core/Constants/PageConstants'
import { GlobalAppStyles } from '@src/GlobalAppStyles'
import { Context } from '@src/Infrastructure/Context'
import { OrganizationProvider } from '@src/Infrastructure/Context/OrganizationContext'
import { localizeArticleUrl, localizeCategoryUrl } from '@src/Infrastructure/Url/localizeUrls/localizeUtilities'
import { getTranslations, modifiedHeroComponents } from '@src/templates/utils'

import { getCategoryAlternateUrls } from '../utils'

const growthbook = new GrowthBook<GrowthBookFeaturesType>({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: `${process.env.GATSBY_GROWTHBOOK_CLIENT_KEY}`,
    subscribeToChanges: true,
    // Only required for A/B testing
    // Called every time a user is put into an experiment
    trackingCallback: growthbookTrackingCallback,
})

const Template = (
    props: PageProps<
        {
            strapiPage: PageNodeType
            allStrapiBlog: { nodes: BlogListItemType[] }
            allStrapiBlogCategory: { nodes: CategoryType[] }
            strapiTranslation: StrapiTranslations
        },
        {
            language: Locale
            prevPage: string
            nextPage: string
            singleTypeComponents: SingleTypeComponentType
            categoryTitle?: string
            alternateUrls?: CategoryType['alternateUrls']
            metaTitle?: CategoryType['metaTitle']
            metaDescription?: CategoryType['metaDescription']
        }
    >
): JSX.Element => {
    const [sendMixpanelEvents, setSendMixpanelEvents] = useState(isGdprCookieAccepted('mixpanel'))

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    })

    const strapiTranslation = props.data.strapiTranslation

    const translations: Translations = getTranslations(strapiTranslation)

    const blogComponent = props.data.strapiPage.pageComponents?.find(
        (p) => p.strapi_component === STRAPI_COMPONENT_TYPES.BLOG_LIST
    ) as BlogListType

    if (blogComponent) {
        // for the category listing page
        blogComponent.sectionTitle = props.pageContext.categoryTitle ?? blogComponent.sectionTitle

        blogComponent.items = localizeArticleUrl(props.data.allStrapiBlog.nodes)
        blogComponent.categories = localizeCategoryUrl(props.data.allStrapiBlogCategory.nodes)
        blogComponent.prevPage = props.pageContext.prevPage
        blogComponent.nextPage = props.pageContext.nextPage
    }

    const pageComponents = props.data.strapiPage.pageComponents ?? []
    const locale = props.pageContext.language

    const singleTypeUSP = props.pageContext.singleTypeComponents['usp'][locale]
    const singleTypeAgilitySection = props.pageContext.singleTypeComponents['agilitySection'][locale]

    const modifiedPageComponents = modifiedHeroComponents({
        pageComponents,
        singleTypeUSP,
        singleTypeAgilitySection,
    })

    const categoryAlternateUrls = props.pageContext.alternateUrls || []

    useEffect(() => {
        // Load features asynchronously when the app renders
        growthbook.loadFeatures()
    }, [])

    return (
        <Context.Provider
            value={{
                locale,
                translations,
                sendMixpanelEvents,
                setSendMixpanelEvents,
            }}
        >
            <OrganizationProvider>
                <QueryClientProvider client={queryClient}>
                    <LayoutProvider>
                        <GrowthBookProvider growthbook={growthbook}>
                            <GlobalAppStyles />
                            <PageBuilder
                                {...{
                                    ...props,
                                    data: {
                                        ...props.data,
                                        strapiPage: {
                                            ...props.data.strapiPage,
                                            pageComponents: modifiedPageComponents,
                                            alternateUrls: getCategoryAlternateUrls(
                                                categoryAlternateUrls,
                                                props.data.strapiPage.alternateUrls
                                            ),
                                            metaTitle: props.pageContext.metaTitle ?? props.data.strapiPage.metaTitle,
                                            metaDescription:
                                                props.pageContext.metaDescription ??
                                                props.data.strapiPage.metaDescription,
                                        },
                                    },
                                }}
                            />
                        </GrowthBookProvider>
                    </LayoutProvider>
                </QueryClientProvider>
            </OrganizationProvider>
        </Context.Provider>
    )
}

export default Template

export const query = graphql`
    query ($id: String!, $language: String!, $skip: Int!, $limit: Int!, $regex: String!) {
        strapiPage(id: { eq: $id }) {
            ...StrapiPageData
        }
        allStrapiBlog(
            filter: {
                language: { eq: $language }
                pageUrl: { regex: "/^((?!do-not-remove).)*$/" }
                blog_categories: { elemMatch: { categoryUrl: { regex: $regex } } }
            }
            sort: { publishedAt: DESC }
            limit: $limit
            skip: $skip
        ) {
            nodes {
                pageUrl
                legacyPageUrl
                useLegacyPageUrl
                title {
                    data {
                        title
                    }
                }
                language
                publishedAt(formatString: "DD MMMM YYYY")
                categories: blog_categories {
                    title
                }
                image {
                    alternativeText
                    localFile {
                        childImageSharp {
                            gatsbyImageData(width: 370)
                        }
                        publicURL
                        extension
                    }
                }
            }
        }
        allStrapiBlogCategory(
            filter: { language: { eq: $language }, categoryUrl: { regex: "/^((?!do-not-remove).)*$/" } }
            sort: { publishedAt: DESC }
        ) {
            nodes {
                title
                categoryUrl
                alternateUrls: alternatives {
                    pageUrl: categoryUrl
                    language
                }
                language
                image {
                    alternativeText
                    localFile {
                        childImageSharp {
                            gatsbyImageData
                        }
                        publicURL
                        extension
                    }
                }
                metaTitle
                metaDescription
            }
        }
        strapiTranslation {
            ...StrapiTranslations
        }
    }
`
